import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser';
import { LogInterface } from '@evorait/pace-logging';
import { EnvironmentService } from '@evorait/pace-environment';

const isIE = /MSIE |Trident\//.test(window.navigator.userAgent);

export function MSALInstanceFactory(
    log: LogInterface,
    environmentService: EnvironmentService,
): IPublicClientApplication {
    const msal = environmentService.msal;
    if (!msal) {
        throw new Error('MSAL: ENVIRONMENT CONFIGURATION IS MISSING!');
    }
    return new PublicClientApplication({
        auth: {
            clientId: msal?.clientId,
            authority: msal?.authority,
            redirectUri: '/',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: isIE,
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Warning,
                loggerCallback: (level, message, containsPii): void => {
                    const logMessage = `MSAL: ${message}`;
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            log.error(logMessage);
                            return;
                        case LogLevel.Info:
                            log.info(logMessage);
                            return;
                        case LogLevel.Warning:
                            log.warn(logMessage);
                            return;
                        case LogLevel.Verbose:
                            log.debug(logMessage);
                            return;
                        case LogLevel.Trace:
                            log.debug(logMessage);
                            return;
                    }
                },
                piiLoggingEnabled: false,
            },
        },
    });
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource (Graph)
export function MSALInterceptorConfigFactory(environmentService: EnvironmentService): MsalInterceptorConfiguration {
    console.log('MSALInterceptorConfigFactory called');
    const msal = environmentService.msal;
    if (!msal) {
        throw new Error('MSAL: ENVIRONMENT CONFIGURATION IS MISSING!');
    }
    const protectedResourceMap = new Map<string, string[]>();
    protectedResourceMap.set(msal.graphApiUrl, [msal.fxAppScope]);
    protectedResourceMap.set(`${environmentService.apiBase}`, [msal.fxAppScope]);
    protectedResourceMap.set(window.location.origin, [msal.fxAppScope]);
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(environmentService: EnvironmentService): MsalGuardConfiguration {
    console.log(' MSALGuardConfigFactory called');
    const msal = environmentService.msal;
    if (!msal) {
        throw new Error('MSAL: ENVIRONMENT CONFIGURATION IS MISSING!');
    }
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [msal.fxAppScope],
        },
    };
}
